/**
 * PurchaseOrderListScreen — list all purchase orders with status filtering.
 *
 * Two entry points:
 *   – "New draft PO" → draft PO form (name+lines, no stock change)
 *   – Row click on ORDERED/PARTIAL PO → ReceivePurchaseOrderScreen
 *
 * Status badges:
 *   ORDERED  → amber (pending receipt)
 *   PARTIAL  → blue  (partially received)
 *   RECEIVED → green (complete)
 */

import { notifySuccess } from '@/store/useToastStore';
import { useErrorPopup } from '@/hooks/useErrorPopup';
import { useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight, Search } from 'lucide-react';
import { database } from '@/db';
import { PurchaseOrder, Supplier, Product } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import { RBACGate, PURCHASING_CREATE_PO } from '@/rbac';
import {
  createDraftPO,
  type PurchaseLine,
} from '@/inventory/PurchaseService';

type StatusFilter = 'ALL' | 'ORDERED' | 'PARTIAL' | 'RECEIVED';

const STATUS_LABELS: Record<string, string> = {
  ORDERED: 'Ordered',
  PARTIAL: 'Partial',
  RECEIVED: 'Received',
};

function statusBadge(status: string) {
  const base = 'rounded-full px-2 py-0.5 text-label uppercase tracking-wide tabular-nums';
  switch (status) {
    case 'ORDERED':  return cn(base, 'bg-warning/15 text-warning-ink');
    case 'PARTIAL':  return cn(base, 'bg-accent/15 text-accent-ink');
    case 'RECEIVED': return cn(base, 'bg-success/15 text-success-ink');
    default:         return cn(base, 'bg-bg-subtle text-fg-tertiary');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────

export function PurchaseOrderListScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);
  const userId = useAppStore((s) => s.currentUser?.id);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [search, setSearch] = useState('');
  const [showDraftForm, setShowDraftForm] = useState(false);

  const purchaseOrders = useObservable<PurchaseOrder[]>(
    () => {
      if (!activeStoreId) return null;
      const conds: any[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
      ];
      if (statusFilter !== 'ALL') {
        conds.push(Q.where('status', statusFilter));
      }
      return database
        .get<PurchaseOrder>('purchase_orders')
        .query(...conds, Q.sortBy('created_at', Q.desc), Q.take(200))
        .observe();
    },
    [activeStoreId, statusFilter],
    [],
  );

  const suppliers = useObservable<Supplier[]>(
    () => merchantId
      ? database.get<Supplier>('suppliers').query(
        Q.where('merchant_id', merchantId),
        Q.where('is_deleted', false),
      ).observe()
      : null,
    [merchantId],
    [],
  );
  const supplierMap = new Map(suppliers.map((s) => [s.id, s.name]));

  const filtered = search.trim()
    ? purchaseOrders.filter((po) =>
        po.poNumber.toLowerCase().includes(search.toLowerCase()) ||
        (supplierMap.get(po.supplierId) ?? '').toLowerCase().includes(search.toLowerCase()),
      )
    : purchaseOrders;

  if (!activeStoreId || !merchantId || !userId) {
    return <div className="text-body text-fg-secondary">No active store. Sign in again.</div>;
  }

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-display font-semibold text-fg-primary">Purchase Orders</h1>
          <p className="mt-1 text-body text-fg-secondary">{filtered.length} orders</p>
        </div>
        <RBACGate requires={PURCHASING_CREATE_PO}>
          <Button onClick={() => setShowDraftForm(true)}>
            <Plus size={16} /> New PO
          </Button>
        </RBACGate>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-xs flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
          <Input
            placeholder="Search PO number or supplier…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1">
          {(['ALL', 'ORDERED', 'PARTIAL', 'RECEIVED'] as StatusFilter[]).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={cn(
                'rounded-md px-3 py-1.5 text-body transition-colors duration-120 ease-apple',
                statusFilter === s
                  ? 'btn-brand border border-white/10'
                  : 'text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary',
              )}
            >
              {s === 'ALL' ? 'All' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        {filtered.length === 0 ? (
          <div className="flex h-40 items-center justify-center text-body text-fg-tertiary">
            {search ? 'No matching orders.' : 'No purchase orders yet.'}
          </div>
        ) : (
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-stroke-hairline/10 text-label uppercase tracking-wide text-fg-tertiary">
                <th className="px-4 py-2.5 text-left">PO Number</th>
                <th className="px-4 py-2.5 text-left">Supplier</th>
                <th className="px-4 py-2.5 text-left">Status</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 text-left">Date</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((po) => {
                const canReceive = po.status === 'ORDERED' || po.status === 'PARTIAL';
                return (
                  <tr
                    key={po.id}
                    className={cn(
                      'border-b border-stroke-hairline/5 transition-colors duration-120 ease-apple',
                      canReceive && 'cursor-pointer hover:bg-bg-subtle',
                    )}
                    onClick={() => canReceive && navigate(`/purchase-orders/${po.id}/receive`)}
                  >
                    <td className="px-4 py-3 font-medium text-fg-primary tabular-nums">
                      {po.poNumber}
                    </td>
                    <td className="px-4 py-3 text-fg-secondary">
                      {supplierMap.get(po.supplierId) ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={statusBadge(po.status)}>{po.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {formatCurrency(po.totalAmount)}
                    </td>
                    <td className="px-4 py-3 text-fg-tertiary tabular-nums">
                      {po.createdAt.toLocaleDateString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canReceive && (
                        <ChevronRight size={16} className="inline-block text-fg-tertiary" />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Draft PO form dialog */}
      <Dialog open={showDraftForm} onOpenChange={(o) => !o && setShowDraftForm(false)}>
        <DialogContent title="New Purchase Order" className="w-[720px]">
          <DraftPOForm
            storeId={activeStoreId}
            merchantId={merchantId}
            userId={userId}
            onDone={() => setShowDraftForm(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  DRAFT PO FORM
// ─────────────────────────────────────────────────────────────────────────────

interface DraftLine {
  product: Product;
  quantity: string;
  unitPrice: string;
  taxRate: string;
}

function DraftPOForm({
  storeId,
  merchantId,
  userId,
  onDone,
}: {
  storeId: string;
  merchantId: string;
  userId: string;
  onDone: () => void;
}) {
  const [supplierSearch, setSupplierSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [notes, setNotes] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorPopup(error, () => setError(null));

  const suppliers = useObservable<Supplier[]>(
    () => {
      if (!merchantId) return null;
      const conds: any[] = [Q.where('merchant_id', merchantId), Q.where('is_deleted', false)];
      if (supplierSearch.trim()) {
        const q = `%${supplierSearch.trim().toLowerCase()}%`;
        conds.push(Q.or(Q.where('name', Q.like(q)), Q.where('phone', Q.like(q))));
      }
      return database.get<Supplier>('suppliers').query(...conds, Q.take(10)).observe();
    },
    [merchantId, supplierSearch],
    [],
  );

  const products = useObservable<Product[]>(
    () => {
      if (!storeId || productSearch.trim().length < 1) return null;
      const q = `%${productSearch.trim().toLowerCase()}%`;
      return database.get<Product>('products').query(
        Q.where('store_id', storeId),
        Q.where('is_deleted', false),
        Q.or(Q.where('name', Q.like(q)), Q.where('sku', Q.like(q))),
        Q.take(10),
      ).observe();
    },
    [storeId, productSearch],
    [],
  );

  function addLine(product: Product) {
    setLines((prev) => {
      const existing = prev.find((l) => l.product.id === product.id);
      if (existing) return prev;
      return [...prev, {
        product,
        quantity: '1',
        unitPrice: String(product.purchasePrice ?? product.mrp ?? 0),
        taxRate: String(product.gstRate ?? 0),
      }];
    });
    setProductSearch('');
  }

  const grandTotal = lines.reduce((sum, l) => {
    const qty = parseFloat(l.quantity) || 0;
    const price = parseFloat(l.unitPrice) || 0;
    const tax = parseFloat(l.taxRate) || 0;
    return sum + qty * price * (1 + tax / 100);
  }, 0);

  async function save() {
    if (!selectedSupplier) { setError('Select a supplier.'); return; }
    if (lines.length === 0) { setError('Add at least one product line.'); return; }
    setSaving(true);
    setError(null);
    const purchaseLines: PurchaseLine[] = lines.map((l) => ({
      product: l.product,
      quantity: parseFloat(l.quantity) || 0,
      unitPrice: parseFloat(l.unitPrice) || 0,
      taxRate: parseFloat(l.taxRate) || 0,
    }));
    const result = await createDraftPO({
      supplier: selectedSupplier,
      storeId,
      lines: purchaseLines,
      notes: notes.trim() || null,
      expectedDate: expectedDate ? new Date(expectedDate) : null,
      createdById: userId,
    });
    setSaving(false);
    if (result.success) {
      notifySuccess('Purchase order saved as a draft.');
      onDone();
    } else {
      setError(result.error ?? 'Failed to create PO.');
    }
  }

  return (
    <div className="space-y-4 p-5">
      {/* Supplier picker */}
      {selectedSupplier ? (
        <div className="flex items-center justify-between rounded-md border border-stroke-hairline/15 bg-bg-canvas px-3 py-2">
          <div>
            <div className="font-medium text-fg-primary">{selectedSupplier.name}</div>
            <div className="text-label text-fg-tertiary">{selectedSupplier.phone ?? selectedSupplier.gstin ?? ''}</div>
          </div>
          <button type="button" className="text-label text-fg-tertiary hover:text-fg-primary" onClick={() => setSelectedSupplier(null)}>
            Change
          </button>
        </div>
      ) : (
        <div className="space-y-1">
          <label className="text-label uppercase tracking-wide text-fg-tertiary">Supplier</label>
          <Input
            placeholder="Search supplier…"
            value={supplierSearch}
            onChange={(e) => setSupplierSearch(e.target.value)}
            autoFocus
          />
          {suppliers.length > 0 && (
            <ul className="rounded-md border border-stroke-hairline/10 bg-bg-canvas text-body">
              {suppliers.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-left hover:bg-bg-subtle"
                    onClick={() => { setSelectedSupplier(s); setSupplierSearch(''); }}
                  >
                    <div className="text-fg-primary">{s.name}</div>
                    <div className="text-label text-fg-tertiary">{s.phone ?? s.gstin ?? ''}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Expected date + notes */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-label uppercase tracking-wide text-fg-tertiary">Expected date</label>
          <Input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
        </div>
        <div className="space-y-1">
          <label className="text-label uppercase tracking-wide text-fg-tertiary">Notes</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </div>
      </div>

      {/* Product search */}
      <div className="space-y-1">
        <label className="text-label uppercase tracking-wide text-fg-tertiary">Add products</label>
        <Input
          placeholder="Search by name or SKU…"
          value={productSearch}
          onChange={(e) => setProductSearch(e.target.value)}
        />
        {products.length > 0 && (
          <ul className="rounded-md border border-stroke-hairline/10 bg-bg-canvas text-body">
            {products.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="w-full px-3 py-2 text-left hover:bg-bg-subtle"
                  onClick={() => addLine(p)}
                >
                  <div className="text-fg-primary">{p.name}</div>
                  <div className="text-label text-fg-tertiary">{p.sku ?? ''} · MRP {formatCurrency(p.mrp ?? 0)}</div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Lines table */}
      {lines.length > 0 && (
        <div className="overflow-auto rounded-md border border-stroke-hairline/10 bg-bg-canvas">
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-stroke-hairline/10 text-label uppercase tracking-wide text-fg-tertiary">
                <th className="px-3 py-2 text-left">Product</th>
                <th className="px-3 py-2 text-right w-24">Qty</th>
                <th className="px-3 py-2 text-right w-28">Unit price</th>
                <th className="px-3 py-2 text-right w-20">GST %</th>
                <th className="px-3 py-2 text-right w-28">Total</th>
                <th className="px-3 py-2 w-8" />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const qty = parseFloat(l.quantity) || 0;
                const price = parseFloat(l.unitPrice) || 0;
                const tax = parseFloat(l.taxRate) || 0;
                const total = qty * price * (1 + tax / 100);
                return (
                  <tr key={l.product.id} className="border-b border-stroke-hairline/5">
                    <td className="px-3 py-2 text-fg-primary">{l.product.name}</td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={l.quantity}
                        onChange={(e) => setLines((prev) => prev.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))}
                        className="w-20 text-right"
                        min="0"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={l.unitPrice}
                        onChange={(e) => setLines((prev) => prev.map((x, i) => i === idx ? { ...x, unitPrice: e.target.value } : x))}
                        className="w-24 text-right"
                        min="0"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        type="number"
                        value={l.taxRate}
                        onChange={(e) => setLines((prev) => prev.map((x, i) => i === idx ? { ...x, taxRate: e.target.value } : x))}
                        className="w-16 text-right"
                        min="0"
                        max="28"
                      />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(total)}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        className="text-danger-ink hover:text-danger-ink/70"
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr className="text-heading font-semibold text-fg-primary">
                <td colSpan={4} className="px-3 py-2.5 text-right">Grand total</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{formatCurrency(grandTotal)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}


      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>Cancel</Button>
        <Button onClick={() => void save()} disabled={saving}>
          {saving ? 'Creating…' : 'Create PO'}
        </Button>
      </div>
    </div>
  );
}
